import * as React from 'react';
import {Plugins, Atom} from '@packages/systems/wfdl';
import {TListOfElement} from '@packages/systems/wfdl-ast';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {mockSubscriptions, placeholderSubscription} from './mockSubscriptions';
import {
  WF_COLLECTION_DATA_KEY,
  WF_TEMPLATE_ID_DATA_KEY,
  WF_TEMPLATE_TYPE,
} from '@packages/systems/commerce/constants';
import {percent} from '@packages/systems/core/utils/WebflowCssSubset';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment, useTemplate} from '@packages/systems/render';
import {getChildren} from '@packages/utilities/data-utils';

const {EXPORT, SSR} = WebflowEnvironments;

const createTemplateId = (elementId: string) => `wf-template-${elementId}`;

const UserAccountSubscriptionList = Atom.createRender((prevProps) => {
  const {
    attributes,
    classes,
    context,
    generateAttributes,
    data,
    renderChild,
    elementId,
  } = prevProps;

  const runtimeEnvironment = useRuntimeEnvironment();

  if (context.get('uiState') !== 'items') {
    return null;
  }

  const listAttributes = attributes.set('role', 'list');

  const children = getChildren(data);
  const template = useTemplate();

  if (runtimeEnvironment === SSR || runtimeEnvironment === EXPORT) {
    // Set a placeholder subscription item with all possible binding fields empty
    const itemContext = context.set('data', placeholderSubscription);

    const content = children.map((child, i) =>
      renderChild(child, itemContext, `subscriptionItem-${i}`, i)
    );

    const templateContent = template(content);
    const templateId = createTemplateId(elementId);
    const tagAttributes = {
      ...generateAttributes(listAttributes, classes),
      [WF_COLLECTION_DATA_KEY]: 'database.userSubscriptions',
      [WF_TEMPLATE_ID_DATA_KEY]: templateId,
    } as const;
    return (
      <>
        <script type={WF_TEMPLATE_TYPE} id={templateId}>
          {templateContent}
        </script>
        <div {...tagAttributes} />
      </>
    );
  }

  return (
    <div {...generateAttributes(listAttributes, classes)}>
      {mockSubscriptions.map((subscription, index) => {
        const itemContext = context.set('data', subscription);
        return children.map((child) => {
          return renderChild(
            child,
            itemContext,
            `subscriptionItem-${index}`,
            index
          );
        });
      })}
    </div>
  );
});

const config = createIntegratedAtom({
  displayName: 'Subscriptions List',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Users'), iconName('AccountSubscriptionList')),
  },
  uiStateOnSelected: 'items',
  render: UserAccountSubscriptionList,
  showEmpty: true,
  constraints: {
    ancestors: [{is: 'UserAccountSubscriptionListWrapper', rule: 'ExactlyOne'}],
    wraps: ['UserAccountSubscriptionListItem'],
    pinToParent: true,
  },
  style: {
    base: {
      main: {
        noPseudo: {
          overflow: 'auto',
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: percent(0),
          WebkitOverflowScrolling: 'touch',
        },
      },
    },
  },
  fields: {
    children: TListOfElement,
  },
});

export default config;
